/**
 * BrainContext — owns the active thread, the live message buffer, and the
 * send/abort machinery. Survives across drawer-open/close + the dedicated
 * /#brain full-screen route so the conversation doesn't reset.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  chatStream,
  listThreads as apiListThreads,
  getThread as apiGetThread,
  deleteThread as apiDeleteThread,
  type BrainStoredMessage,
  type BrainStreamEvent,
  type BrainThread,
} from '../lib/brain';

export type BrainModel = 'claude-sonnet-4-6' | 'claude-haiku-4-5-20251001';

export interface LiveMessage {
  /** Local-only id — DB ids arrive after the round trip. */
  localId: string;
  role: 'user' | 'assistant' | 'tool';
  text: string;
  toolEvents: Array<{ name: string; input?: Record<string, unknown>; ok?: boolean; error?: string; ms?: number }>;
  pending: boolean;
}

interface BrainContextValue {
  threads: BrainThread[];
  activeThreadId: string | null;
  messages: LiveMessage[];
  pending: boolean;
  error: string | null;
  model: BrainModel;
  setModel: (m: BrainModel) => void;
  refreshThreads: () => Promise<void>;
  openThread: (id: string) => Promise<void>;
  newThread: () => void;
  sendMessage: (text: string) => Promise<void>;
  stop: () => void;
  removeThread: (id: string) => Promise<void>;
}

const BrainContext = createContext<BrainContextValue | undefined>(undefined);

function hydrateFromStored(stored: BrainStoredMessage[]): LiveMessage[] {
  return stored.flatMap((m): LiveMessage[] => {
    if (m.role === 'user') {
      return [{ localId: m.id, role: 'user', text: m.content, toolEvents: [], pending: false }];
    }
    if (m.role === 'assistant') {
      const toolEvents = (m.tool_calls ?? []).map((tc) => ({ name: tc.name, input: tc.input, ok: true }));
      return [{ localId: m.id, role: 'assistant', text: m.content, toolEvents, pending: false }];
    }
    return [];
  });
}

export function BrainProvider({ children }: { children: ReactNode }) {
  const [threads, setThreads] = useState<BrainThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<LiveMessage[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [model, setModel] = useState<BrainModel>('claude-sonnet-4-6');
  const abortRef = useRef<AbortController | null>(null);

  const refreshThreads = useCallback(async () => {
    try {
      const list = await apiListThreads();
      setThreads(list);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const openThread = useCallback(async (id: string) => {
    setError(null);
    try {
      const { thread, messages: stored } = await apiGetThread(id);
      setActiveThreadId(thread.id);
      setMessages(hydrateFromStored(stored));
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const newThread = useCallback(() => {
    abortRef.current?.abort();
    setActiveThreadId(null);
    setMessages([]);
    setError(null);
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || pending) return;
    setError(null);

    const userLocalId = `u-${Date.now()}`;
    const assistantLocalId = `a-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { localId: userLocalId, role: 'user', text, toolEvents: [], pending: false },
      { localId: assistantLocalId, role: 'assistant', text: '', toolEvents: [], pending: true },
    ]);

    setPending(true);
    abortRef.current = new AbortController();

    const updateAssistant = (mutator: (m: LiveMessage) => LiveMessage) => {
      setMessages((prev) => prev.map((m) => (m.localId === assistantLocalId ? mutator(m) : m)));
    };

    try {
      await chatStream({
        thread_id: activeThreadId ?? undefined,
        message: text,
        model,
        signal: abortRef.current.signal,
        onEvent: (event: BrainStreamEvent) => {
          if (event.type === 'thread') {
            setActiveThreadId(event.id);
          } else if (event.type === 'tool_use') {
            updateAssistant((m) => ({
              ...m,
              toolEvents: [...m.toolEvents, { name: event.name, input: event.input, ok: undefined }],
            }));
          } else if (event.type === 'tool_result') {
            updateAssistant((m) => {
              const idx = [...m.toolEvents].reverse().findIndex((t) => t.name === event.name && t.ok === undefined);
              if (idx === -1) return m;
              const realIdx = m.toolEvents.length - 1 - idx;
              const updated = [...m.toolEvents];
              updated[realIdx] = { ...updated[realIdx], ok: event.ok, error: event.error, ms: event.ms };
              return { ...m, toolEvents: updated };
            });
          } else if (event.type === 'delta') {
            updateAssistant((m) => ({ ...m, text: m.text + event.text }));
          } else if (event.type === 'done') {
            updateAssistant((m) => ({ ...m, pending: false }));
          } else if (event.type === 'error') {
            setError(event.message);
            updateAssistant((m) => ({ ...m, pending: false }));
          }
        },
      });
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError((err as Error).message);
      }
      updateAssistant((m) => ({ ...m, pending: false }));
    } finally {
      setPending(false);
      abortRef.current = null;
      // Refresh thread list so the new conversation appears in the rail.
      refreshThreads();
    }
  }, [activeThreadId, model, pending, refreshThreads]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setPending(false);
    setMessages((prev) => prev.map((m) => (m.pending ? { ...m, pending: false } : m)));
  }, []);

  const removeThread = useCallback(async (id: string) => {
    try {
      await apiDeleteThread(id);
      setThreads((prev) => prev.filter((t) => t.id !== id));
      if (activeThreadId === id) {
        setActiveThreadId(null);
        setMessages([]);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, [activeThreadId]);

  const value = useMemo<BrainContextValue>(() => ({
    threads, activeThreadId, messages, pending, error, model, setModel,
    refreshThreads, openThread, newThread, sendMessage, stop, removeThread,
  }), [threads, activeThreadId, messages, pending, error, model, refreshThreads, openThread, newThread, sendMessage, stop, removeThread]);

  return <BrainContext.Provider value={value}>{children}</BrainContext.Provider>;
}

export function useBrain() {
  const ctx = useContext(BrainContext);
  if (!ctx) throw new Error('useBrain must be used within a BrainProvider');
  return ctx;
}
